import React, { useState } from 'react';
import { Rocket, Code2, ArrowRight, Wrench, Brain, CheckCircle2, GitBranch, Clock } from 'lucide-react';
import ContactModal from '../components/ContactModal';

const Projects = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState('');

  const handleDiscussProject = (projectName: string) => {
    setSelectedProject(projectName);
    setIsModalOpen(true);
  };

  const projectOptions = [
    {
      name: "Launch Sprint",
      price: "Starts at $7,500",
      target: "For: Startups, internal teams, and rapid MVP builds",
      description: "A fast, fixed-scope project to turn your idea into working software. Perfect for building MVPs, internal tools, or proving a concept.",
      includes: [
        "2–3 week timeline",
        "PM + 1–2 developers",
        "Optional UI/UX design",
        "Working, testable product",
        "Clear scope, clean handoff"
      ],
      deliverables: [
        "Web or mobile MVP",
        "Customer portal or dashboard",
        "Internal automation tools"
      ]
    },
    {
      name: "Custom Build Projects",
      price: "Typically $12,000–$50,000+",
      target: "For: Companies with a clear product or platform vision",
      description: "When you need more than a sprint: we design, build, and deliver full-featured applications or business tools, milestone by milestone.",
      includes: [
        "Discovery & technical planning",
        "Project-managed sprints",
        "Backend, frontend, or full-stack delivery",
        "Optional QA, design, or DevOps",
        "Fixed-price or phased billing"
      ],
      deliverables: [
        "SaaS platforms",
        "Portals with logins, dashboards, or scheduling",
        "APIs, admin tools, or custom workflows"
      ],
      note: "Perfect for serious software initiatives without long-term retainer requirements."
    },
    {
      name: "Platform Rescue & Modernization",
      price: "Assessment starts at $2,500 | Full projects $8,000–$60,000+",
      target: "For: Companies with legacy systems, broken builds, or dev handoff issues",
      description: "Stabilize, modernize, or fully rebuild your existing platform. We make sense of outdated systems, untangle messy codebases, and bring your product up to speed.",
      includes: [
        "Code + infrastructure audit",
        "Fix or rebuild plan",
        "PM + dev support throughout",
        "Optional migration or redesign",
        "CI/CD & deployment setup (if needed)"
      ],
      situations: [
        "Lost or unreliable past dev teams",
        "Insecure or unstable platforms",
        "Need to move from legacy stacks (e.g. Classic ASP, PHP 5.x)"
      ]
    },
    {
      name: "Technical Discovery & Architecture Consulting",
      price: "$1,500–$5,000",
      target: "For: Teams that need clarity before building",
      description: "We help you define what to build, how to build it, and where to start.",
      includes: [
        "1–2 strategy sessions with a senior architect/PM",
        "Review of business goals and technical constraints",
        "Feature list and roadmap planning",
        "Tech stack and infrastructure recommendations",
        "Optional risk/feasibility analysis for complex builds"
      ],
      deliverables: [
        "Wireframes or UI outlines",
        "Tech stack recommendations",
        "Milestone or sprint plan",
        "Dev estimate and timeline"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-24 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-purple-600/20" />
        </div>
        <div className="relative container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h1 className="text-5xl font-bold mb-6">Project Solutions</h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              From quick sprints to full-scale builds, we have the right solution for your software project.
              Choose the engagement model that fits your needs, timeline, and budget.
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-6">Why Work With Us?</h2>
              <p className="text-lg text-gray-600">
                We combine technical excellence with business acumen to deliver solutions
                that drive real value. Our approach is collaborative, transparent, and
                focused on your long-term success.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: <Clock className="w-8 h-8 text-yellow-400" />,
                  title: "Fast Delivery",
                  description: "Get your project from concept to production quickly with our efficient development process."
                },
                {
                  icon: <GitBranch className="w-8 h-8 text-yellow-400" />,
                  title: "Quality Code",
                  description: "We write clean, maintainable code that scales with your business needs."
                },
                {
                  icon: <Brain className="w-8 h-8 text-yellow-400" />,
                  title: "Expert Team",
                  description: "Work with senior developers who understand both technology and business."
                }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="inline-block p-3 bg-white rounded-lg shadow-sm mb-4">
                    {item.icon}
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Project Options */}
      <section className="py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl font-bold mb-6">Choose Your Path</h2>
            <p className="text-lg text-gray-600">
              Whether you need a quick MVP or a full-scale enterprise solution,
              we have a project option that fits your goals.
            </p>
          </div>
          <div className="space-y-16">
            {projectOptions.map((option, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="grid lg:grid-cols-2 gap-12">
                  <div>
                    <div className="mb-6">
                      <h2 className="text-3xl font-bold mb-2">{option.name}</h2>
                      <div className="text-2xl text-yellow-600 font-semibold mb-2">{option.price}</div>
                      <div className="text-gray-600 font-medium">{option.target}</div>
                    </div>
                    <p className="text-gray-600 mb-6">{option.description}</p>
                    <div className="mb-8">
                      <h3 className="font-semibold mb-4">What's Included:</h3>
                      <ul className="space-y-3">
                        {option.includes?.map((item, idx) => (
                          <li key={idx} className="flex items-start space-x-3">
                            <CheckCircle2 className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                            <span className="text-gray-600">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    {option.note && (
                      <p className="text-gray-600 italic">{option.note}</p>
                    )}
                  </div>
                  <div>
                    {option.deliverables && (
                      <div className="bg-slate-50 rounded-lg p-6 mb-6">
                        <h3 className="font-semibold mb-4">Common Deliverables:</h3>
                        <ul className="space-y-3">
                          {option.deliverables.map((item, idx) => (
                            <li key={idx} className="flex items-start space-x-3">
                              <ArrowRight className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                              <span className="text-gray-600">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {option.situations && (
                      <div className="bg-slate-50 rounded-lg p-6">
                        <h3 className="font-semibold mb-4">Common Situations:</h3>
                        <ul className="space-y-3">
                          {option.situations.map((item, idx) => (
                            <li key={idx} className="flex items-start space-x-3">
                              <ArrowRight className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                              <span className="text-gray-600">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <div className="mt-8">
                      <button
                        onClick={() => handleDiscussProject(option.name)}
                        className="inline-flex items-center px-6 py-3 bg-slate-900 text-white rounded-lg font-semibold hover:bg-slate-800 transition-all group"
                      >
                        Discuss Your Project
                        <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DaaS Transition Section */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-6">Looking Beyond the Project</h2>
              <p className="text-lg text-gray-600">
                Need ongoing support after your project? Transition seamlessly to our
                Development as a Service (DaaS) model for continuous improvement and maintenance.
              </p>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="grid md:grid-cols-2 gap-12">
                <div>
                  <h3 className="text-2xl font-bold mb-6">Why Consider DaaS?</h3>
                  <ul className="space-y-4">
                    {[
                      "Continuous development without hiring full-time staff",
                      "Flexible scaling based on your needs",
                      "Dedicated team that knows your codebase",
                      "Predictable monthly costs",
                      "Strategic technical partnership"
                    ].map((point, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <CheckCircle2 className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                        <span className="text-gray-600">{point}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8">
                    <a
                      href="/daas"
                      className="inline-flex items-center px-6 py-3 bg-yellow-400 text-slate-900 rounded-lg font-semibold hover:bg-yellow-300 transition-all group"
                    >
                      Learn More About DaaS
                      <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </div>
                </div>
                <div className="bg-slate-50 rounded-lg p-6">
                  <h3 className="font-semibold mb-4">Smooth Transition Process:</h3>
                  <ul className="space-y-4">
                    {[
                      "Complete knowledge transfer from project team",
                      "Tailored DaaS plan based on your needs",
                      "Same high-quality development standards",
                      "Continuous improvement focus",
                      "Regular strategy sessions to align with your goals"
                    ].map((item, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <ArrowRight className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                        <span className="text-gray-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Ready to Start Your Project?</h2>
            <p className="text-xl text-gray-300 mb-8">
              Let's discuss your needs and find the perfect solution for your business.
            </p>
            <button
              onClick={() => handleDiscussProject('Custom Project')}
              className="inline-flex items-center px-8 py-4 bg-yellow-400 text-slate-900 rounded-lg font-semibold hover:bg-yellow-300 transition-all group"
            >
              Schedule a Consultation
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>

      {/* Contact Modal */}
      <ContactModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        selectedPlan={selectedProject}
      />
    </div>
  );
};

export default Projects;